import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import MemberAvatar from '@/components/MemberAvatar';
import usePageMeta from '@/hooks/usePageMeta';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import RichTextEditor from '@/components/RichTextEditor';
import RichText from '@/components/RichText';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FlaskConical, Users, MessageSquare, FileText, Target, UserPlus, UserMinus, Send, Link2, Plus, CheckCircle, X, Clock, Trophy, MapPin, Sparkles, HelpCircle, Wrench, LayoutDashboard, BarChart3, ArrowUpRight, ThumbsUp, ArrowLeft, Heart, Trash2, Flag, Pencil, Save, Calendar as CalendarIcon, GitBranch, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { SponsorBanner } from '@/components/SponsorBanner';
import PageSideRail from '@/components/PageSideRail';
import ReportDialog from '@/components/ReportDialog';
import { MentionInput, RenderMentions } from '@/components/MentionInput';
import LinkPreviews from '@/components/LinkPreviews';
import ReactionBar from '@/components/ReactionBar';
import BriteLabCalendarTab from '@/components/BriteLabCalendarTab';
import MemberProfileModal from '@/components/MemberProfileModal';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel } from '@/components/ui/dropdown-menu';

const BACKEND_URL = '';

const COUNTRIES_MAP = {
  GB: '🇬🇧', IE: '🇮🇪', FR: '🇫🇷', DE: '🇩🇪', NL: '🇳🇱', BE: '🇧🇪', ES: '🇪🇸', IT: '🇮🇹',
  SE: '🇸🇪', NO: '🇳🇴', DK: '🇩🇰', FI: '🇫🇮', PL: '🇵🇱', CH: '🇨🇭', AT: '🇦🇹', PT: '🇵🇹',
  US: '🇺🇸', CA: '🇨🇦', AU: '🇦🇺', NZ: '🇳🇿', IN: '🇮🇳', CN: '🇨🇳', JP: '🇯🇵', SG: '🇸🇬',
  AE: '🇦🇪', ZA: '🇿🇦', BR: '🇧🇷', MX: '🇲🇽', XX: '🌍',
};

const OUTCOME_TYPES = [
  { value: 'operational_improvement', label: 'Operational Improvement' },
  { value: 'knowledge_gap', label: 'Knowledge Gap Filled' },
  { value: 'cost_reduction', label: 'Cost Reduction' },
  { value: 'collaboration', label: 'Collaboration Achieved' },
];

const PRIORITY_STYLES = {
  high:   { className: 'bg-red-100 text-red-700 border-red-200',     label: 'High' },
  medium: { className: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Medium' },
  low:    { className: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Low' },
};

const IMPACT_LEVELS = [
  { value: 'individual',     label: 'Individual' },
  { value: 'organisation',   label: 'Organisation' },
  { value: 'industry',       label: 'Industry' },
  { value: 'cross_industry', label: 'Cross-Industry' },
];

function PriorityPill({ priority }) {
  const cfg = PRIORITY_STYLES[priority] || PRIORITY_STYLES.medium;
  return (
    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${cfg.className}`} data-testid={`priority-pill-${priority || 'medium'}`}>
      {cfg.label}
    </span>
  );
}

export default function BriteLabDetailPage({ user }) {
  const { labId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [lab, setLab] = useState(null);
  usePageMeta(lab ? {
    title: lab.title || 'BriteLab',
    description: (lab.description || lab.problem_statement || '').slice(0, 200),
  } : { title: 'BriteLab' });
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msgInput, setMsgInput] = useState('');
  const [researchUrl, setResearchUrl] = useState('');
  const [uploadingResearch, setUploadingResearch] = useState(false);
  const researchFileInputRef = useRef(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showSolve, setShowSolve] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [recommendedUsers, setRecommendedUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedInvites, setSelectedInvites] = useState([]);
  const [inviteTab, setInviteTab] = useState('recommended');
  const [emailInvites, setEmailInvites] = useState('');
  // Research folders state
  const [folders, setFolders] = useState([]);
  const [deleteResearchConfirm, setDeleteResearchConfirm] = useState(null);
  const [activeFolderId, setActiveFolderId] = useState('all');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [solveForm, setSolveForm] = useState({ outcome_type: '', solution_summary: '', status: 'solved' });
  const [milestones, setMilestones] = useState([]);
  const [hasPlan, setHasPlan] = useState(false);
  const [milestoneSearch, setMilestoneSearch] = useState({});
  const [milestoneDropdown, setMilestoneDropdown] = useState(null);
  const [labParticipants, setLabParticipants] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [forumPosts, setForumPosts] = useState([]);
  const [activePost, setActivePost] = useState(null);
  const [recommendedReads, setRecommendedReads] = useState([]);
  const [showNewPost, setShowNewPost] = useState(false);
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostContent, setNewPostContent] = useState('');
  const [commentInput, setCommentInput] = useState('');
  const [reportTarget, setReportTarget] = useState(null);
  const [viewingMember, setViewingMember] = useState(null);
  // Mini Projects state — sub-workstreams that sit beneath a parent
  // BriteLab. Loaded only when this lab is itself a parent (no
  // `parent_lab_id`), so we never recurse one level deeper.
  const [minis, setMinis] = useState([]);
  const [showCreateMini, setShowCreateMini] = useState(false);
  const [creatingMini, setCreatingMini] = useState(false);
  const [miniForm, setMiniForm] = useState({
    root_cause: '', lab_type: 'problem', title: '', problem_statement: '',
    category: '', desired_outcome: '', country: '', impact_level: '',
  });
  const [labCategories, setLabCategories] = useState([]);
  const messagesEnd = useRef(null);

  const token = sessionStorage.getItem('session_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  const canInteract = lab?.status === 'active';
  const isOwner = lab?.created_by === user?.user_id;
  const isAdmin = user?.role === 'admin';
  const isInvitedMember = lab?.invited_users?.includes(user?.user_id);
  const canEdit = isOwner || isAdmin || isInvitedMember;

  useEffect(() => { loadLab(); loadMessages(); loadDashboard(); loadForumPosts(); loadRecommendedReads(); loadFolders(); loadMinis(); loadLabCategories(); }, [labId]);
  useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => {
    // Close milestone dropdown on outside click
    const handleClick = () => setMilestoneDropdown(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const loadLab = () => {
    fetch(`${BACKEND_URL}/api/britelabs/${labId}`, { credentials: 'include', headers })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => {
        setLab(d);
        // Migrate old milestones: convert string 'owner' to 'owners' array
        const migratedMs = (d.milestones || []).map(m => {
          if (m.owner && !m.owners) return { ...m, owners: [{ user_id: '', name: m.owner }], description: m.success_criteria || m.description || '' };
          return { ...m, owners: m.owners || [], description: m.success_criteria || m.description || '' };
        });
        setMilestones(migratedMs);
        setHasPlan(d.has_project_plan);
        setLoading(false);
        if (d.has_project_plan) {
          const memberIds = new Set([...(d.invited_users || []), d.created_by].filter(Boolean));
          fetch(`${BACKEND_URL}/api/members`, { credentials: 'include', headers })
            .then(r => r.json()).then(users => setLabParticipants(users.filter(u => memberIds.has(u.user_id)))).catch(() => {});
        }
      })
      .catch(() => { toast.error('BriteLab not found or access denied'); navigate('/britelabs'); });
  };

  const loadMessages = () => {
    fetch(`${BACKEND_URL}/api/britelabs/${labId}/messages`, { credentials: 'include', headers })
      .then(r => r.json()).then(setMessages).catch(() => {});
  };

  const loadDashboard = () => {
    fetch(`${BACKEND_URL}/api/britelabs/${labId}/dashboard`, { credentials: 'include', headers })
      .then(r => { if (r.ok) return r.json(); throw new Error(); })
      .then(setDashboard)
      .catch(() => {});
  };

  const handleRemoveParticipant = async (p) => {
    const ok = await window.appConfirm(`Remove ${p.name} from this BriteLab? They will lose access to all lab content.`);
    if (!ok) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/britelabs/${labId}/members/${p.user_id}`, {
        method: 'DELETE', credentials: 'include', headers,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to remove');
      toast.success(`${p.name} has been removed`);
      loadLab();
      loadDashboard();
    } catch (e) {
      toast.error(e.message || 'Failed to remove member');
    }
  };

  const loadFolders = () => {
    fetch(`${BACKEND_URL}/api/britelabs/${labId}/folders`, { credentials: 'include', headers })
      .then(r => r.ok ? r.json() : [])
      .then(setFolders)
      .catch(() => {});
  };

  // Pull all Mini Projects sitting under this lab. The endpoint returns
  // [] for minis themselves (they can't have grandchildren) and for any
  // lab the user can't see. We don't gate the fetch on `lab` being
  // loaded — backend will 404 cleanly if the parent vanishes.
  const loadMinis = () => {
    fetch(`${BACKEND_URL}/api/britelabs/${labId}/minis`, { credentials: 'include', headers })
      .then(r => r.ok ? r.json() : [])
      .then(d => setMinis(Array.isArray(d) ? d : []))
      .catch(() => {});
  };

  const loadLabCategories = () => {
    fetch(`${BACKEND_URL}/api/britelabs/categories`, { credentials: 'include', headers })
      .then(r => r.ok ? r.json() : [])
      .then(d => setLabCategories(Array.isArray(d) ? d : []))
      .catch(() => {});
  };

  const handleCreateMini = async () => {
    if (!miniForm.title.trim() || !miniForm.problem_statement.trim() || !miniForm.category.trim() || !miniForm.root_cause.trim()) {
      toast.error('Please fill in title, root cause, problem statement and category');
      return;
    }
    setCreatingMini(true);
    try {
      const payload = { ...miniForm, parent_lab_id: labId };
      const res = await fetch(`${BACKEND_URL}/api/britelabs`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to create Mini Project');
      toast.success('Mini Project created');
      setShowCreateMini(false);
      setMiniForm({ root_cause: '', lab_type: 'problem', title: '', problem_statement: '', category: '', desired_outcome: '', country: '', impact_level: '' });
      loadMinis();
    } catch (e) {
      toast.error(e.message || 'Failed to create Mini Project');
    } finally {
      setCreatingMini(false);
    }
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/britelabs/${labId}/folders`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ name }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to create');
      toast.success(`Folder "${name}" created`);
      setNewFolderName(''); setShowNewFolder(false);
      loadFolders();
    } catch (e) {
      toast.error(e.message || 'Failed to create folder');
    }
  };

  const handleRenameFolder = async (folder) => {
    const newName = window.prompt(`Rename folder "${folder.name}" to:`, folder.name);
    if (!newName || newName === folder.name) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/britelabs/${labId}/folders/${folder.folder_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ name: newName.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success('Folder renamed');
      loadFolders();
    } catch (e) {
      toast.error(e.message || 'Failed to rename');
    }
  };

  const handleDeleteFolder = async (folder) => {
    const ok = await window.appConfirm(`Delete the "${folder.name}" folder? Files inside will move to Uncategorised.`);
    if (!ok) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/britelabs/${labId}/folders/${folder.folder_id}`, {
        method: 'DELETE', credentials: 'include', headers,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(`"${folder.name}" deleted`);
      if (activeFolderId === folder.folder_id) setActiveFolderId('all');
      loadFolders();
      loadMessages();
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    }
  };

  const handleMoveResearch = async (researchMsg, targetFolderId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/britelabs/${labId}/research/${researchMsg.message_id}/move`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ folder_id: targetFolderId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to move');
      toast.success(`Moved to ${data.folder_name}`);
      loadMessages();
      loadFolders();
    } catch (e) {
      toast.error(e.message || 'Failed to move');
    }
  };

  const handleDeleteResearch = async (researchMsg) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/britelabs/${labId}/research/${researchMsg.message_id}`, {
        method: 'DELETE', credentials: 'include', headers,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to delete');
      toast.success(`Deleted "${data.filename || researchMsg.filename || 'file'}". An admin can restore it.`);
      setDeleteResearchConfirm(null);
      loadMessages();
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    }
  };

  const loadForumPosts = () => {
    fetch(`${BACKEND_URL}/api/britelabs/${labId}/posts`, { credentials: 'include', headers })
      .then(r => { if (r.ok) return r.json(); throw new Error(); })
      .then(setForumPosts)
      .catch(() => {});
  };

  const loadRecommendedReads = () => {
    fetch(`${BACKEND_URL}/api/britelabs/${labId}/recommended-reads`, { credentials: 'include', headers })
      .then(r => { if (r.ok) return r.json(); throw new Error(); })
      .then(d => setRecommendedReads(d.reads || []))
      .catch(() => {});
  };

  const openPost = async (post) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/britelabs/${labId}/posts/${post.post_id}`, { credentials: 'include', headers });
      if (res.ok) setActivePost(await res.json());
    } catch {}
  };

  const createPost = async () => {
    if (!newPostContent.trim()) { toast.error('Content is required'); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/britelabs/${labId}/posts`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ title: newPostTitle, content: newPostContent }),
      });
      if (res.ok) {
        toast.success('Post created!');
        setShowNewPost(false); setNewPostTitle(''); setNewPostContent('');
        loadForumPosts();
      }
    } catch { toast.error('Failed to post'); }
  };

  const togglePostLike = async (postId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/britelabs/${labId}/posts/${postId}/like`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', ...headers },
      });
      if (res.ok) {
        const data = await res.json();
        setForumPosts(prev => prev.map(p => p.post_id === postId ? { ...p, liked_by_me: data.liked, like_count: data.like_count } : p));
        if (activePost?.post_id === postId) setActivePost(prev => ({ ...prev, liked_by_me: data.liked, like_count: data.like_count }));
      }
    } catch { /* ignore */ }
  };

  const reactToBLPost = async (postId, reactionType) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/britelabs/${labId}/posts/${postId}/react`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ reaction: reactionType }),
      });
      if (!res.ok) return;
      const data = await res.json();
      setForumPosts(prev => prev.map(p => p.post_id === postId
        ? { ...p, my_reaction: data.my_reaction, reaction_counts: data.reaction_counts } : p));
      if (activePost?.post_id === postId) {
        setActivePost(prev => ({ ...prev, my_reaction: data.my_reaction, reaction_counts: data.reaction_counts }));
      }
    } catch { /* ignore */ }
  };

  const reactToBLComment = async (commentId, reactionType) => {
    if (!activePost) return;
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/britelabs/${labId}/posts/${activePost.post_id}/comments/${commentId}/react`,
        {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({ reaction: reactionType }),
        },
      );
      if (!res.ok) return;
      const data = await res.json();
      setActivePost(prev => prev ? {
        ...prev,
        comments: (prev.comments || []).map(c => c.comment_id === commentId
          ? { ...c, my_reaction: data.my_reaction, reaction_counts: data.reaction_counts } : c),
      } : prev);
    } catch { /* ignore */ }
  };

  const addComment = async () => {
    if (!commentInput.trim() || !activePost) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/britelabs/${labId}/posts/${activePost.post_id}/comments`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ content: commentInput }),
      });
      if (res.ok) {
        setCommentInput('');
        openPost(activePost); // refresh
        loadForumPosts(); // update comment count
      }
    } catch { toast.error('Failed to comment'); }
  };

  const deletePost = async (postId) => {
    if (!(await window.appConfirm('Delete this post?'))) return;
    try {
      await fetch(`${BACKEND_URL}/api/britelabs/${labId}/posts/${postId}`, {
        method: 'DELETE', credentials: 'include', headers,
      });
      setActivePost(null);
      loadForumPosts();
      toast.success('Post deleted');
    } catch {}
  };

  const [editingBLPost, setEditingBLPost] = useState(null);
  const [editBLForm, setEditBLForm] = useState({ title: '', content: '' });

  const handleEditBLPost = async () => {
    if (!editBLForm.content.trim()) { toast.error('Content is required'); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/britelabs/${labId}/posts/${editingBLPost.post_id}`, {
        method: 'PUT', credentials: 'include', headers,
        body: JSON.stringify(editBLForm),
      });
      if (res.ok) {
        const updated = await res.json();
        if (activePost?.post_id === updated.post_id) setActivePost(prev => ({ ...prev, ...updated }));
        loadForumPosts();
        setEditingBLPost(null);
        toast.success('Post updated');
      } else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed to update'); }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  const sendMessage = async (type = 'discussion') => {
    const content = type === 'research' ? researchUrl : msgInput;
    if (!content.trim()) return;
    try {
      const body = { content, type };
      if (type === 'research') {
        body.research_url = researchUrl;
        // Drop new links into the active folder if one is selected
        if (activeFolderId && activeFolderId !== 'all') body.folder_id = activeFolderId;
      }
      await fetch(`${BACKEND_URL}/api/britelabs/${labId}/messages`, {
        method: 'POST', credentials: 'include', headers, body: JSON.stringify(body),
      });
      if (type === 'research') setResearchUrl(''); else setMsgInput('');
      loadMessages();
      if (type === 'research') loadFolders();
    } catch { toast.error('Failed to send'); }
  };

  const inviteUsers = async () => {
    const emails = emailInvites.split(/[,;\n]+/).map(e => e.trim()).filter(Boolean);
    if (!selectedInvites.length && !emails.length) return;
    try {
      await fetch(`${BACKEND_URL}/api/britelabs/${labId}/invite`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ user_ids: selectedInvites, emails }),
      });
      toast.success('Stakeholders invited');
      setShowInvite(false);
      setSelectedInvites([]);
      setEmailInvites('');
      loadLab();
    } catch { toast.error('Failed to invite'); }
  };

  const saveMilestones = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/britelabs/${labId}/milestones`, {
        method: 'PUT', credentials: 'include', headers,
        body: JSON.stringify({ milestones, has_project_plan: hasPlan }),
      });
      toast.success('Project plan saved');
    } catch { toast.error('Failed to save'); }
  };

  const handleSolve = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/britelabs/${labId}/solve`, {
        method: 'PUT', credentials: 'include', headers, body: JSON.stringify(solveForm),
      });
      toast.success('BriteLab resolved!');
      setShowSolve(false);
      loadLab();
    } catch { toast.error('Failed'); }
  };

  const openInviteDialog = async () => {
    try {
      const [usersRes, recRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/members`, { credentials: 'include', headers }),
        fetch(`${BACKEND_URL}/api/britelabs/${labId}/recommend-stakeholders`, { credentials: 'include', headers }).catch(() => null),
      ]);
      if (usersRes?.ok) {
        const users = await usersRes.json();
        setAllUsers(users.filter(u => !lab.invited_users?.includes(u.user_id)));
      }
      try {
        if (recRes?.ok) {
          const rec = await recRes.json();
          setRecommendedUsers(rec.recommended || []);
        } else {
          setRecommendedUsers([]);
        }
      } catch {
        setRecommendedUsers([]);
      }
      setInviteTab('recommended');
      setShowInvite(true);
    } catch (err) {
      toast.error('Failed to load invite data');
    }
  };

  const addMilestone = () => setShowMilestoneDialog(true);
  const [showMilestoneDialog, setShowMilestoneDialog] = useState(false);
  const [newMilestone, setNewMilestone] = useState({ title: '', owners: [], due_date: '', description: '', status: 'pending', priority: 'medium' });
  const [showArchivedMilestones, setShowArchivedMilestones] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [detailsForm, setDetailsForm] = useState({});

  const [newMilestoneSearch, setNewMilestoneSearch] = useState('');
  const [newMilestoneDropdownOpen, setNewMilestoneDropdownOpen] = useState(false);

  const addNewMilestoneOwner = (member) => {
    setNewMilestone(p => {
      const owners = p.owners || [];
      if (owners.find(o => o.user_id === member.user_id)) return p;
      return { ...p, owners: [...owners, { user_id: member.user_id, name: member.name, picture: member.picture }] };
    });
    setNewMilestoneSearch('');
    setNewMilestoneDropdownOpen(false);
  };

  const removeNewMilestoneOwner = (uid) => {
    setNewMilestone(p => ({ ...p, owners: (p.owners || []).filter(o => o.user_id !== uid) }));
  };

  const confirmAddMilestone = () => {
    setMilestones([...milestones, { ...newMilestone }]);
    setNewMilestone({ title: '', owners: [], due_date: '', description: '', status: 'pending', priority: 'medium' });
    setNewMilestoneSearch('');
    setShowMilestoneDialog(false);
  };

  const startEditDetails = () => {
    setDetailsForm({
      root_cause: lab?.root_cause || '',
      desired_outcome: lab?.desired_outcome || '',
      description: lab?.problem_statement || '',
    });
    setEditingDetails(true);
  };

  const saveDetails = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/britelabs/${labId}/details`, {
        method: 'PUT', credentials: 'include', headers,
        body: JSON.stringify(detailsForm),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
      setEditingDetails(false);
      toast.success('Details updated');
      loadLab();
    } catch (e) { toast.error(e.message); }
  };

  const updateMilestone = (i, field, val) => { const m = [...milestones]; m[i][field] = val; setMilestones(m); };
  const removeMilestone = (i) => setMilestones(milestones.filter((_, idx) => idx !== i));
  const addMilestoneOwner = (i, member) => {
    const m = [...milestones];
    const owners = m[i].owners || [];
    if (!owners.find(o => o.user_id === member.user_id)) {
      m[i].owners = [...owners, { user_id: member.user_id, name: member.name, picture: member.picture }];
      setMilestones(m);
    }
  };
  const removeMilestoneOwner = (i, userId) => {
    const m = [...milestones];
    m[i].owners = (m[i].owners || []).filter(o => o.user_id !== userId);
    setMilestones(m);
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-12 px-6 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-navy" />
      </main>
    </div>
  );

  if (!lab) return null;

  // Pending invitation summary view
  if (lab.summary_only || lab.my_invite_status === 'pending_invite') {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <main className="pt-24 pb-12 px-6">
          <div className="container mx-auto max-w-2xl">
            <button onClick={() => navigate('/britelabs')} className="text-sm text-brand-navy hover:underline mb-4 inline-block">&larr; Back to BriteLabs</button>
            <Card className="overflow-hidden border-0 shadow-lg" data-testid="invite-summary-card">
              <div className="bg-gradient-to-r from-brand-navy to-brand-sage p-6">
                <div className="flex items-center gap-2 mb-3">
                  <FlaskConical className="h-5 w-5 text-white/80" />
                  <span className="text-white/70 text-sm font-medium">BriteLab Invitation</span>
                </div>
                <h1 className="text-2xl font-bold text-white">{lab.title}</h1>
                <p className="text-white/70 text-sm mt-1">BriteLab Owner: {lab.creator_name}</p>
              </div>
              <div className="p-6 space-y-5">
                <div className="flex items-center gap-3 flex-wrap">
                  {lab.category && <Badge variant="outline">{lab.category}</Badge>}
                  {lab.lab_type && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      lab.lab_type === 'question' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'
                    }`}>
                      {lab.lab_type === 'question' ? 'Question' : 'Problem'}
                    </span>
                  )}
                  {lab.impact_level && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-50 text-violet-700 capitalize">
                      {lab.impact_level.replace(/_/g, ' ')}
                    </span>
                  )}
                  {lab.country && COUNTRIES_MAP[lab.country] && <span className="text-base">{COUNTRIES_MAP[lab.country]}</span>}
                </div>

                {lab.root_cause && (
                  <div>
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Root Cause</h3>
                    <RichText html={lab.root_cause} className="text-sm text-slate-700 leading-relaxed compact" />
                  </div>
                )}

                <div>
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Problem Statement</h3>
                  <RichText html={lab.problem_statement} className="text-sm text-slate-700 leading-relaxed compact" />
                </div>

                {lab.desired_outcome && (
                  <div>
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Desired Outcome</h3>
                    <RichText html={lab.desired_outcome} className="text-sm text-slate-700 leading-relaxed compact" />
                  </div>
                )}

                <div className="flex items-center gap-4 text-xs text-slate-500 pt-2 border-t">
                  <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {lab.kpis?.stakeholder_count || 1} stakeholders</span>
                  <span>{new Date(lab.created_at).toLocaleDateString()}</span>
                </div>

                <div className="flex gap-3 pt-3">
                  <Button data-testid="accept-invite-btn" onClick={async () => {
                    try {
                      await fetch(`${BACKEND_URL}/api/britelabs/${labId}/accept-invite`, {
                        method: 'POST', credentials: 'include', headers
                      });
                      toast.success('Invitation accepted! You now have full access.');
                      loadLab();
                    } catch { toast.error('Failed to accept'); }
                  }} className="flex-1 bg-brand-sage hover:bg-brand-sage/90 text-white">
                    <CheckCircle className="h-4 w-4 mr-1.5" /> Accept Invitation
                  </Button>
                  <Button data-testid="decline-invite-btn" variant="outline" onClick={async () => {
                    try {
                      await fetch(`${BACKEND_URL}/api/britelabs/${labId}/decline-invite`, {
                        method: 'POST', credentials: 'include', headers
                      });
                      toast.success('Invitation declined.');
                      navigate('/britelabs');
                    } catch { toast.error('Failed to decline'); }
                  }} className="flex-1 text-slate-600 border-slate-200">
                    <X className="h-4 w-4 mr-1.5" /> Decline
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  const filteredInviteUsers = allUsers.filter(u =>
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) || u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-12 px-3 md:px-6">
        <div className="container mx-auto max-w-7xl">
          <SponsorBanner placement={`britelab:${labId}`} type="top" className="mb-3 md:mb-4 flex justify-center" />
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0">
          <div className="mb-4 md:mb-6">
            <button onClick={() => navigate(lab.parent_lab_id ? `/britelabs/${lab.parent_lab_id}` : '/britelabs')} className="text-sm text-brand-navy hover:underline mb-2 inline-block">
              {lab.parent_lab_id ? <>&larr; Back to parent BriteLab{lab.parent_title ? `: ${lab.parent_title}` : ''}</> : <>&larr; Back to BriteLabs</>}
            </button>
            {lab.parent_lab_id && (
              <div className="flex items-center gap-1.5 text-xs text-violet-700 bg-violet-50 border border-violet-100 rounded-full px-2.5 py-1 mb-2 w-fit" data-testid="mini-project-breadcrumb">
                <GitBranch className="h-3.5 w-3.5" /> Mini Project
                {lab.parent_title && <><ChevronRight className="h-3 w-3" /> <span className="font-medium">{lab.parent_title}</span></>}
              </div>
            )}
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                  <Badge variant="outline" className="text-[11px]">{lab.category}</Badge>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${lab.status === 'active' ? 'bg-brand-sage/20 text-brand-sage' : lab.status === 'solved' ? 'bg-brand-blue/20 text-brand-navy' : 'bg-slate-100 text-slate-600'}`}>
                    {lab.status.charAt(0).toUpperCase() + lab.status.slice(1)}
                  </span>
                  {lab.lab_type && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      lab.lab_type === 'question' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'
                    }`}>
                      {lab.lab_type === 'question' ? <HelpCircle className="h-3 w-3" /> : <Wrench className="h-3 w-3" />}
                      {lab.lab_type === 'question' ? 'Question' : 'Problem'}
                    </span>
                  )}
                  {lab.impact_level && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-100 capitalize">
                      {lab.impact_level.replace(/_/g, ' ')}
                    </span>
                  )}
                  {lab.country && COUNTRIES_MAP[lab.country] && (
                    <span className="text-base leading-none">{COUNTRIES_MAP[lab.country]}</span>
                  )}
                </div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold font-heading text-slate-900">{lab.title}</h1>
                <p className="text-sm text-slate-500 mt-1">BriteLab Owner: {lab.creator_name} · Created {new Date(lab.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {(isOwner || isAdmin) && lab.status === 'pending' && (
                  <>
                    <Button data-testid="approve-from-detail-btn" onClick={async () => {
                      await fetch(`${BACKEND_URL}/api/britelabs/${labId}/approve`, { method: 'PUT', credentials: 'include', headers });
                      toast.success('BriteLab approved'); loadLab();
                    }} size="sm" className="bg-brand-sage hover:bg-brand-sage text-white">
                      <CheckCircle className="h-4 w-4 mr-1" /> Approve
                    </Button>
                    <Button data-testid="reject-from-detail-btn" onClick={async () => {
                      await fetch(`${BACKEND_URL}/api/britelabs/${labId}/reject`, { method: 'PUT', credentials: 'include', headers });
                      toast.success('BriteLab rejected'); navigate('/britelabs');
                    }} size="sm" variant="outline" className="text-red-600">
                      <X className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </>
                )}
                {canEdit && canInteract && (
                  <Button data-testid="invite-btn" onClick={openInviteDialog} variant="outline" size="sm">
                    <UserPlus className="h-4 w-4 mr-1" /> Invite
                  </Button>
                )}
                {canEdit && canInteract && (
                  <Button data-testid="solve-btn" onClick={() => setShowSolve(true)} size="sm"
                    className="bg-brand-sage hover:bg-brand-sage text-white">
                    <CheckCircle className="h-4 w-4 mr-1" /> Mark Solved
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Root Cause, Problem & Outcome */}
          {!editingDetails ? (
            <>
              {lab.root_cause && (
                <Card className="p-3 md:p-5 border border-slate-200 mb-3 md:mb-4">
                  <h3 className="text-xs md:text-sm font-bold text-slate-500 uppercase tracking-wide mb-1.5">Root Cause</h3>
                  <RichText html={lab.root_cause} className="text-sm md:text-base text-slate-700" />
                </Card>
              )}
              <div className="grid md:grid-cols-3 gap-3 md:gap-4 mb-2 md:mb-3">
                <Card className="p-3 md:p-5 md:col-span-2 border border-slate-200">
                  <h3 className="text-xs md:text-sm font-bold text-slate-500 uppercase tracking-wide mb-1.5">Problem Statement</h3>
                  <RichText html={lab.problem_statement} className="text-sm md:text-base text-slate-700" />
                </Card>
                <Card className="p-3 md:p-5 border border-slate-200">
                  <h3 className="text-xs md:text-sm font-bold text-slate-500 uppercase tracking-wide mb-1.5">Desired Outcome</h3>
                  {lab.desired_outcome
                    ? <RichText html={lab.desired_outcome} className="text-sm text-slate-700" />
                    : <p className="text-sm text-slate-400 italic">Not specified</p>}
                </Card>
              </div>
              {canEdit && (
                <div className="mb-4">
                  <Button variant="outline" size="sm" onClick={startEditDetails} data-testid="edit-details-btn" className="text-teal-600 hover:bg-teal-50">
                    <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit Descriptions
                  </Button>
                </div>
              )}
            </>
          ) : (
            <Card className="p-4 md:p-6 border border-teal-200 mb-4 space-y-3" data-testid="edit-details-form">
              <div>
                <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Root Cause</Label>
                <RichTextEditor value={detailsForm.root_cause} onChange={v => setDetailsForm(p => ({ ...p, root_cause: v }))} testId="edit-root-cause" minHeight="6rem" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Problem Statement</Label>
                <RichTextEditor value={detailsForm.description} onChange={v => setDetailsForm(p => ({ ...p, description: v }))} testId="edit-problem-statement" minHeight="7rem" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Desired Outcome</Label>
                <RichTextEditor value={detailsForm.desired_outcome} onChange={v => setDetailsForm(p => ({ ...p, desired_outcome: v }))} testId="edit-desired-outcome" minHeight="6rem" />
              </div>
              <div className="flex gap-2">
                <Button onClick={saveDetails} className="bg-brand-navy text-white" data-testid="save-details-btn">
                  <Save className="h-4 w-4 mr-1.5" /> Save
                </Button>
                <Button variant="outline" onClick={() => setEditingDetails(false)}>Cancel</Button>
              </div>
            </Card>
          )}

          {/* Tabs */}
          <Tabs defaultValue="dashboard">
            <TabsList className="mb-4 w-full overflow-x-auto flex-nowrap justify-start md:justify-center">
              <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="text-xs md:text-sm px-2.5 md:px-3"><LayoutDashboard className="h-4 w-4 md:mr-1" /> <span className="hidden md:inline">Dashboard</span></TabsTrigger>
              <TabsTrigger value="discussion" className="text-xs md:text-sm px-2.5 md:px-3"><MessageSquare className="h-4 w-4 md:mr-1" /> <span className="hidden md:inline">Discussion</span></TabsTrigger>
              <TabsTrigger value="research" className="text-xs md:text-sm px-2.5 md:px-3"><FileText className="h-4 w-4 md:mr-1" /> <span className="hidden md:inline">Research</span></TabsTrigger>
              <TabsTrigger value="plan" className="text-xs md:text-sm px-2.5 md:px-3"><Target className="h-4 w-4 md:mr-1" /> <span className="hidden md:inline">Project Plan</span></TabsTrigger>
              <TabsTrigger value="calendar" data-testid="tab-calendar" className="text-xs md:text-sm px-2.5 md:px-3"><CalendarIcon className="h-4 w-4 md:mr-1" /> <span className="hidden md:inline">Diary</span></TabsTrigger>
              {!lab.parent_lab_id && (
                <TabsTrigger value="minis" data-testid="tab-minis" className="text-xs md:text-sm px-2.5 md:px-3">
                  <GitBranch className="h-4 w-4 md:mr-1" />
                  <span className="hidden md:inline">Mini Projects</span>
                  {minis.length > 0 && (
                    <span className="ml-1.5 px-1.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold">{minis.length}</span>
                  )}
                </TabsTrigger>
              )}
            </TabsList>

            {/* Dashboard Tab */}
            <TabsContent value="dashboard">
              {dashboard ? (
                <div className="space-y-5" data-testid="britelab-dashboard">
                  {/* Top KPIs */}
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-2 md:gap-3">
                    <Card className="p-2.5 md:p-4 text-center border border-slate-200">
                      <Users className="h-4 w-4 md:h-5 md:w-5 text-brand-navy mx-auto mb-0.5 md:mb-1" />
                      <p className="text-lg md:text-2xl font-bold text-slate-800">{dashboard.summary.total_stakeholders}</p>
                      <p className="text-[10px] md:text-xs text-slate-500">Participants</p>
                    </Card>
                    <Card className="p-2.5 md:p-4 text-center border border-slate-200">
                      <MessageSquare className="h-4 w-4 md:h-5 md:w-5 text-violet-600 mx-auto mb-0.5 md:mb-1" />
                      <p className="text-lg md:text-2xl font-bold text-slate-800">{dashboard.summary.total_messages}</p>
                      <p className="text-[10px] md:text-xs text-slate-500">Messages</p>
                    </Card>
                    <Card className="p-2.5 md:p-4 text-center border border-slate-200">
                      <FileText className="h-4 w-4 md:h-5 md:w-5 text-brand-sage mx-auto mb-0.5 md:mb-1" />
                      <p className="text-lg md:text-2xl font-bold text-slate-800">{dashboard.summary.research_links}</p>
                      <p className="text-[10px] md:text-xs text-slate-500">Research</p>
                    </Card>
                    <Card className="p-2.5 md:p-4 text-center border border-slate-200">
                      <Target className="h-4 w-4 md:h-5 md:w-5 text-amber-600 mx-auto mb-0.5 md:mb-1" />
                      <p className="text-lg md:text-2xl font-bold text-slate-800">{dashboard.summary.milestones_done}/{dashboard.summary.milestones_total}</p>
                      <p className="text-[10px] md:text-xs text-slate-500">Milestones</p>
                    </Card>
                    <Card className="p-2.5 md:p-4 text-center border border-slate-200">
                      <Clock className="h-4 w-4 md:h-5 md:w-5 text-slate-500 mx-auto mb-0.5 md:mb-1" />
                      <p className="text-lg md:text-2xl font-bold text-slate-800">{dashboard.days_open}</p>
                      <p className="text-[10px] md:text-xs text-slate-500">Days {lab.status === 'solved' ? 'to Solve' : 'Open'}</p>
                    </Card>
                  </div>

                  {/* Progress bar */}
                  {dashboard.summary.milestones_total > 0 && (
                    <Card className="p-3 md:p-5 border border-slate-200">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-bold text-slate-700">Overall Progress</h3>
                        <span className="text-sm font-bold text-brand-navy">{dashboard.summary.progress_pct}%</span>
                      </div>
                      <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{
                          width: `${dashboard.summary.progress_pct}%`,
                          background: dashboard.summary.progress_pct === 100 ? '#4a9b5e' : 'linear-gradient(90deg, #3E60B1, #9BC269)',
                        }} />
                      </div>
                      <div className="flex gap-4 mt-2 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-brand-sage" /> {dashboard.summary.milestones_done} done</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> {dashboard.summary.milestones_in_progress} in progress</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-200" /> {dashboard.summary.milestones_total - dashboard.summary.milestones_done - dashboard.summary.milestones_in_progress} pending</span>
                      </div>
                    </Card>
                  )}

                  {/* Activity Chart */}
                  {dashboard.activity_timeline.length > 0 && (
                    <Card className="p-3 md:p-5 border border-slate-200">
                      <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                        <BarChart3 className="h-4 w-4 text-slate-400" /> Activity Timeline
                      </h3>
                      <div className="flex items-end gap-1 h-24">
                        {dashboard.activity_timeline.map((d, i) => {
                          const max = Math.max(...dashboard.activity_timeline.map(x => x.messages));
                          const pct = max > 0 ? (d.messages / max) * 100 : 0;
                          return (
                            <div key={i} className="flex-1 group relative" title={`${d.date}: ${d.messages} messages`}>
                              <div className="w-full rounded-t transition-all"
                                style={{ height: `${Math.max(pct, 4)}%`, background: 'linear-gradient(180deg, #3E60B1, #6C9BEB)' }} />
                              <div className="absolute -top-7 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap">
                                {d.date.slice(5)}: {d.messages}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-between mt-1 text-[10px] text-slate-400">
                        <span>{dashboard.activity_timeline[0]?.date.slice(5)}</span>
                        <span>{dashboard.activity_timeline[dashboard.activity_timeline.length - 1]?.date.slice(5)}</span>
                      </div>
                    </Card>
                  )}

                  {/* Participants Table */}
                  <Card className="border border-slate-200 overflow-hidden">
                    <div className="px-3 md:px-5 py-3 border-b border-slate-100">
                      <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                        <Users className="h-4 w-4 text-slate-400" /> Participants ({dashboard.participants.length})
                      </h3>
                    </div>
                    <div className="divide-y divide-slate-50">
                      {dashboard.participants.map((p, i) => (
                        <button
                          type="button"
                          key={p.user_id}
                          data-testid={`participant-row-${p.user_id}`}
                          onClick={() => setViewingMember(p)}
                          className="w-full text-left flex items-center gap-2.5 md:gap-4 px-3 md:px-5 py-2.5 md:py-3 hover:bg-slate-50/80 hover:ring-1 hover:ring-brand-navy/10 transition-all">
                          {/* Rank */}
                          <span className="text-xs font-bold text-slate-300 w-5 text-center">{i + 1}</span>
                          {/* Avatar — shared MemberAvatar so the brand
                              gradient + black-initial fallback is consistent
                              across every member-avatar surface. */}
                          <MemberAvatar name={p.name} picture={p.picture} size={36} />
                          {/* Name + Role */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-semibold text-slate-800 truncate">{p.name}</span>
                              {p.is_creator && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-brand-navy/10 text-brand-navy">OWNER</span>}
                              {p.country && COUNTRIES_MAP[p.country] && <span className="text-sm">{COUNTRIES_MAP[p.country]}</span>}
                            </div>
                            {(p.role || p.organisation) && (
                              <p className="text-xs text-slate-400 truncate">{[p.role, p.organisation].filter(Boolean).join(' at ')}</p>
                            )}
                          </div>
                          {/* Stats */}
                          <div className="hidden md:flex items-center gap-5">
                            <div className="text-center" title="Discussion messages">
                              <p className="text-sm font-bold text-slate-700">{p.discussion_count}</p>
                              <p className="text-[10px] text-slate-400">Messages</p>
                            </div>
                            <div className="text-center" title="Research links shared">
                              <p className="text-sm font-bold text-slate-700">{p.research_count}</p>
                              <p className="text-[10px] text-slate-400">Research</p>
                            </div>
                            <div className="text-center" title="Milestones completed">
                              <p className="text-sm font-bold text-slate-700">{p.milestones_completed}/{p.milestones_owned}</p>
                              <p className="text-[10px] text-slate-400">Milestones</p>
                            </div>
                          </div>
                          {/* Last active */}
                          <div className="text-right hidden sm:block">
                            {p.last_active ? (
                              <p className="text-xs text-slate-400" title={p.last_active}>
                                {(() => {
                                  const diff = Date.now() - new Date(p.last_active).getTime();
                                  const mins = Math.floor(diff / 60000);
                                  if (mins < 60) return `${mins}m ago`;
                                  const hrs = Math.floor(mins / 60);
                                  if (hrs < 24) return `${hrs}h ago`;
                                  return `${Math.floor(hrs / 24)}d ago`;
                                })()}
                              </p>
                            ) : (
                              <p className="text-xs text-slate-300">No activity</p>
                            )}
                          </div>
                          {/* Owner-only remove (not visible on the owner row) */}
                          {isOwner && !p.is_creator && (
                            <span
                              role="button"
                              tabIndex={0}
                              data-testid={`remove-participant-${p.user_id}`}
                              onClick={(e) => { e.stopPropagation(); handleRemoveParticipant(p); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleRemoveParticipant(p); } }}
                              title="Remove from BriteLab"
                              className="opacity-60 hover:opacity-100 hover:bg-rose-50 text-rose-500 rounded-md p-1.5 transition-all flex-shrink-0">
                              <UserMinus className="h-4 w-4" />
                            </span>
                          )}
                        </button>
                      ))}
                      {dashboard.participants.length === 0 && (
                        <div className="py-8 text-center text-sm text-slate-400">No participants yet</div>
                      )}
                    </div>
                  </Card>

                  {/* Milestone Progress */}
                  {dashboard.milestones.length > 0 && (
                    <Card className="border border-slate-200 overflow-hidden">
                      <div className="px-3 md:px-5 py-3 border-b border-slate-100">
                        <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                          <Target className="h-4 w-4 text-slate-400" /> Milestone Tracker
                        </h3>
                      </div>
                      <div className="divide-y divide-slate-50">
                        {dashboard.milestones.map((m, i) => (
                          <div key={i} className="flex items-center gap-2.5 md:gap-3 px-3 md:px-5 py-2.5 md:py-3">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                              m.status === 'complete' ? 'bg-brand-sage/15 text-brand-sage' : m.status === 'in_progress' ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-400'
                            }`}>
                              {m.status === 'complete' ? <CheckCircle className="h-4 w-4" /> : <span className="text-xs font-bold">{i + 1}</span>}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className={`text-sm font-medium ${m.status === 'complete' ? 'text-slate-500 line-through' : 'text-slate-800'}`}>{m.title || `Milestone ${i + 1}`}</p>
                                {m.priority && <PriorityPill priority={m.priority} />}
                              </div>
                              <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
                                {m.owners?.length > 0 ? (
                                  <span className="flex items-center gap-1">
                                    {m.owners.map(o => o.name).join(', ')}
                                  </span>
                                ) : m.owner ? <span>{m.owner}</span> : null}
                                {m.due_date && <span>Due: {m.due_date}</span>}
                              </div>
                            </div>
                            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                              m.status === 'complete' ? 'bg-brand-sage/15 text-brand-sage' : m.status === 'in_progress' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500'
                            }`}>{m.status?.replace(/_/g, ' ')}</span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}
                </div>
              ) : (
                <div className="flex justify-center py-12">
                  <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full" />
                </div>
              )}
            </TabsContent>

            {/* Discussion Tab (Forum-style) */}
            <TabsContent value="discussion">
              {activePost ? (
                /* Post Detail View */
                <div data-testid="post-detail-view">
                  <button onClick={() => setActivePost(null)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand-navy mb-4 transition-colors" data-testid="back-to-posts">
                    <ArrowLeft className="h-4 w-4" /> Back to discussions
                  </button>
                  <Card className="p-4 md:p-6 border border-slate-200 mb-4">
                    <div className="flex items-start gap-3">
                      <button type="button" onClick={() => activePost.author_id && setViewingMember({ user_id: activePost.author_id, name: activePost.author_name, picture: activePost.author_picture })}
                        className="flex-shrink-0 hover:ring-2 hover:ring-brand-navy/20 rounded-full transition-all"
                        data-testid={`post-author-avatar-${activePost.post_id}`} title="View profile">
                        <MemberAvatar name={activePost.author_name} picture={activePost.author_picture} size={40} />
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <button type="button"
                            onClick={() => activePost.author_id && setViewingMember({ user_id: activePost.author_id, name: activePost.author_name, picture: activePost.author_picture })}
                            className="font-semibold text-sm text-slate-800 hover:text-brand-navy hover:underline transition-colors">
                            {activePost.author_name}
                          </button>
                          {activePost.author_role === 'admin' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-navy/10 text-brand-navy font-bold">Admin</span>}
                          <span className="text-xs text-slate-400">{timeAgo(activePost.created_at)}</span>
                        </div>
                        {activePost.title && !editingBLPost && <h3 className="text-lg font-bold text-slate-800 mt-2">{activePost.title}</h3>}
                        {editingBLPost?.post_id === activePost.post_id ? (
                          <div className="space-y-3 mt-2" data-testid="edit-bl-post-form">
                            <Input value={editBLForm.title} onChange={e => setEditBLForm({ ...editBLForm, title: e.target.value })}
                              placeholder="Title (optional)" data-testid="edit-bl-post-title" />
                            <MentionInput
                              value={editBLForm.content}
                              onChange={(v) => setEditBLForm({ ...editBLForm, content: v })}
                              testId="edit-bl-post-content"
                              rows={4}
                              placeholder="Use @ to mention users, # for companies"
                            />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={handleEditBLPost} className="bg-brand-navy text-white" data-testid="save-bl-edit-btn">Save Changes</Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingBLPost(null)}>Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm text-slate-600 mt-2 whitespace-pre-line leading-relaxed"><RenderMentions text={activePost.content} /></p>
                            <LinkPreviews text={activePost.content} />
                            {activePost.edited_at && <span className="text-[10px] text-slate-400 italic">(edited {timeAgo(activePost.edited_at)})</span>}
                          </>
                        )}
                        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100">
                          <ReactionBar
                            myReaction={activePost.my_reaction}
                            counts={activePost.reaction_counts}
                            onReact={(type) => reactToBLPost(activePost.post_id, type)}
                            testIdPrefix={`bl-post-${activePost.post_id}`}
                            size="sm"
                          />
                          <span className="text-sm text-slate-400 flex items-center gap-1">
                            <MessageSquare className="h-4 w-4" /> {activePost.comment_count} comment{activePost.comment_count !== 1 ? 's' : ''}
                          </span>
                          {(activePost.author_id === user?.user_id || isAdmin) && (
                            <>
                              <button onClick={() => { setEditingBLPost(activePost); setEditBLForm({ title: activePost.title || '', content: activePost.content || '' }); }}
                                className="text-sm text-slate-400 hover:text-brand-navy flex items-center gap-1" data-testid="edit-bl-post-btn">
                                <Pencil className="h-3.5 w-3.5" /> Edit
                              </button>
                              <button onClick={() => deletePost(activePost.post_id)} className="text-sm text-slate-400 hover:text-red-500 ml-auto flex items-center gap-1">
                                <Trash2 className="h-3.5 w-3.5" /> Delete
                              </button>
                            </>
                          )}
                          {activePost.author_id !== user?.user_id && (
                            <button data-testid="britelab-post-report-btn"
                              onClick={() => setReportTarget({ userId: activePost.author_id, userName: activePost.author_name, contextType: 'britelab_post', contextId: activePost.post_id })}
                              className="text-sm text-slate-300 hover:text-red-500 ml-auto flex items-center gap-1 transition-colors"
                              title="Report Member">
                              <Flag className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Comments */}
                  <div className="space-y-3 mb-4">
                    {(activePost.comments || []).map(c => (
                      <div key={c.comment_id} className="flex items-start gap-3 pl-4 border-l-2 border-slate-100" data-testid={`comment-${c.comment_id}`}>
                        <button type="button"
                          onClick={() => c.author_id && setViewingMember({ user_id: c.author_id, name: c.author_name, picture: c.author_picture })}
                          className="mt-0.5 flex-shrink-0 hover:ring-2 hover:ring-brand-navy/20 rounded-full transition-all"
                          title="View profile">
                          {c.author_picture ? (
                            <MemberAvatar name={c.author_name} picture={c.author_picture} size={28} />
                          ) : (
                            <MemberAvatar name={c.author_name} size={28} />
                          )}
                        </button>
                        <div>
                          <div className="flex items-center gap-2">
                            <button type="button"
                              onClick={() => c.author_id && setViewingMember({ user_id: c.author_id, name: c.author_name, picture: c.author_picture })}
                              className="text-sm font-semibold text-slate-700 hover:text-brand-navy hover:underline transition-colors">
                              {c.author_name}
                            </button>
                            {c.author_role === 'admin' && <span className="text-[9px] px-1 py-0 rounded bg-brand-navy/10 text-brand-navy font-bold">Admin</span>}
                            <span className="text-xs text-slate-400">{timeAgo(c.created_at)}</span>
                          </div>
                          <p className="text-sm text-slate-600 mt-0.5 whitespace-pre-line"><RenderMentions text={c.content} /></p>
                          <LinkPreviews text={c.content} />
                          <div className="mt-1.5">
                            <ReactionBar
                              myReaction={c.my_reaction}
                              counts={c.reaction_counts}
                              onReact={(type) => reactToBLComment(c.comment_id, type)}
                              testIdPrefix={`bl-comment-${c.comment_id}`}
                              size="xs"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add comment */}
                  {canInteract && (
                    <div className="space-y-2">
                      <MentionInput
                        value={commentInput}
                        onChange={setCommentInput}
                        onSubmit={addComment}
                        placeholder="Write a comment... Use @ to mention users, # for companies"
                        testId="comment-input"
                        rows={2}
                      />
                      <div className="flex justify-end">
                        <Button onClick={addComment} size="sm" className="bg-brand-navy text-white" data-testid="submit-comment-btn"
                          disabled={!commentInput.trim()}>
                          <Send className="h-4 w-4 mr-1.5" /> Send
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Posts List View */
                <div data-testid="discussion-posts-list">
                  {canInteract && (
                    <div className="mb-4">
                      {showNewPost ? (
                        <Card className="p-5 border border-slate-200 space-y-3" data-testid="new-post-form">
                          <Input placeholder="Title (optional)" value={newPostTitle}
                            onChange={e => setNewPostTitle(e.target.value)} data-testid="new-post-title" />
                          <MentionInput
                            value={newPostContent}
                            onChange={setNewPostContent}
                            placeholder="Share your thoughts, ideas, or questions with the team... Use @ to mention users, # for companies"
                            testId="new-post-content"
                            rows={4}
                          />
                          <div className="flex gap-2 justify-end">
                            <Button variant="ghost" size="sm" onClick={() => { setShowNewPost(false); setNewPostTitle(''); setNewPostContent(''); }}>Cancel</Button>
                            <Button size="sm" onClick={createPost} className="bg-brand-navy text-white" data-testid="submit-post-btn">Post</Button>
                          </div>
                        </Card>
                      ) : (
                        <button onClick={() => setShowNewPost(true)} data-testid="start-discussion-btn"
                          className="w-full text-left p-4 rounded-xl border border-dashed border-slate-200 hover:border-brand-navy/30 hover:bg-slate-50 transition-all text-sm text-slate-400">
                          <Plus className="h-4 w-4 inline mr-2" /> Start a new discussion...
                        </button>
                      )}
                    </div>
                  )}

                  {forumPosts.length === 0 ? (
                    <Card className="p-10 text-center border border-slate-200">
                      <MessageSquare className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-400">No discussions yet. Be the first to start one!</p>
                    </Card>
                  ) : (
                    <div className="space-y-3">
                      {forumPosts.map(post => (
                        <Card key={post.post_id} data-testid={`discussion-post-${post.post_id}`}
                          className="p-5 hover:shadow-md transition-shadow cursor-pointer border border-slate-200"
                          onClick={() => openPost(post)}>
                          <div className="flex items-start gap-3">
                            <button type="button"
                              onClick={(e) => { e.stopPropagation(); post.author_id && setViewingMember({ user_id: post.author_id, name: post.author_name, picture: post.author_picture }); }}
                              className="flex-shrink-0 hover:ring-2 hover:ring-brand-navy/20 rounded-full transition-all"
                              title="View profile">
                              {post.author_picture ? (
                                <MemberAvatar name={post.author_name} picture={post.author_picture} size={36} />
                              ) : (
                                <MemberAvatar name={post.author_name} size={36} />
                              )}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <button type="button"
                                  onClick={(e) => { e.stopPropagation(); post.author_id && setViewingMember({ user_id: post.author_id, name: post.author_name, picture: post.author_picture }); }}
                                  className="font-semibold text-sm text-slate-800 hover:text-brand-navy hover:underline transition-colors">
                                  {post.author_name}
                                </button>
                                {post.author_role === 'admin' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-navy/10 text-brand-navy font-bold">Admin</span>}
                                <span className="text-xs text-slate-400">{timeAgo(post.created_at)}</span>
                              </div>
                              {post.title && <h3 className="font-bold text-slate-800 mt-1">{post.title}</h3>}
                              <p className="text-sm text-slate-600 mt-1 line-clamp-2"><RenderMentions text={post.content} /></p>
                              <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                                {(() => {
                                  const counts = post.reaction_counts || {};
                                  const total = Object.values(counts).reduce((a, b) => a + (b || 0), 0) || post.like_count || 0;
                                  if (total === 0) return null;
                                  return (
                                    <span className={`flex items-center gap-1 ${post.my_reaction ? 'text-brand-navy' : ''}`}>
                                      <Heart className={`h-3.5 w-3.5 ${post.my_reaction ? 'fill-brand-navy text-brand-navy' : ''}`} /> {total}
                                    </span>
                                  );
                                })()}
                                <span className="flex items-center gap-1">
                                  <MessageSquare className="h-3.5 w-3.5" /> {post.comment_count || 0} comment{(post.comment_count || 0) !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Research Tab */}
            <TabsContent value="research">
              <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4">
                {/* Folder sidebar */}
                <Card className="border border-slate-200 p-3 h-fit md:sticky md:top-24" data-testid="research-folders-sidebar">
                  <div className="flex items-center justify-between mb-2 px-1">
                    <h4 className="text-xs uppercase tracking-wide font-bold text-slate-500">Folders</h4>
                    {(isOwner || isAdmin) && (
                      <Button size="sm" variant="ghost" onClick={() => setShowNewFolder(v => !v)} title="New folder"
                        data-testid="new-folder-toggle" className="h-6 w-6 p-0">
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                  {showNewFolder && (isOwner || isAdmin) && (
                    <div className="flex gap-1 mb-2 px-1">
                      <Input value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleCreateFolder()}
                        placeholder="Folder name" className="h-7 text-xs" data-testid="new-folder-input" />
                      <Button size="sm" className="h-7 px-2 bg-brand-navy text-white" onClick={handleCreateFolder}
                        data-testid="new-folder-save">OK</Button>
                    </div>
                  )}
                  <div className="space-y-0.5">
                    <button
                      onClick={() => setActiveFolderId('all')}
                      data-testid="folder-all"
                      className={`w-full flex items-center justify-between px-2 py-1.5 rounded-md text-sm text-left transition-colors ${
                        activeFolderId === 'all' ? 'bg-brand-navy/10 text-brand-navy font-medium' : 'text-slate-600 hover:bg-slate-50'
                      }`}>
                      <span className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> All files</span>
                      <span className="text-[10px] text-slate-400">{messages.filter(m => m.type === 'research').length}</span>
                    </button>
                    {folders.map(f => (
                      <div key={f.folder_id} className="group flex items-center">
                        <button
                          onClick={() => setActiveFolderId(f.folder_id)}
                          data-testid={`folder-${f.folder_id}`}
                          className={`flex-1 flex items-center justify-between px-2 py-1.5 rounded-md text-sm text-left transition-colors ${
                            activeFolderId === f.folder_id ? 'bg-brand-navy/10 text-brand-navy font-medium' : 'text-slate-600 hover:bg-slate-50'
                          }`}>
                          <span className="flex items-center gap-1.5 truncate">
                            <FileText className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="truncate">{f.name}</span>
                            {f.is_default && <span className="text-[8px] uppercase tracking-wide text-slate-400">default</span>}
                          </span>
                          <span className="text-[10px] text-slate-400 ml-1">{f.file_count}</span>
                        </button>
                        {(isOwner || isAdmin) && !f.is_default && (
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 flex">
                            <button onClick={() => handleRenameFolder(f)} title="Rename"
                              data-testid={`rename-folder-${f.folder_id}`}
                              className="text-slate-400 hover:text-brand-navy p-0.5"><Pencil className="h-3 w-3" /></button>
                            <button onClick={() => handleDeleteFolder(f)} title="Delete"
                              data-testid={`delete-folder-${f.folder_id}`}
                              className="text-slate-400 hover:text-rose-500 p-0.5 ml-0.5"><Trash2 className="h-3 w-3" /></button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>

                {/* File list */}
                <Card className="border border-slate-200 p-5">
                  <div className="space-y-3 mb-4">
                  {(() => {
                    const defaultFolderId = folders.find(f => f.is_default)?.folder_id;
                    const filtered = messages
                      .filter(m => m.type === 'research')
                      .filter(m => activeFolderId === 'all' || (m.folder_id || defaultFolderId) === activeFolderId);
                    if (filtered.length === 0) {
                      return <p className="text-sm text-slate-400 text-center py-6">No research files in this folder yet.</p>;
                    }
                    return filtered.map(m => {
                    const url = m.research_url || m.content || '';
                    const isUpload = !!m.doc_id || url.startsWith('/api/britelabs/documents/');
                    const customTitle = (m.content && m.content !== m.research_url) ? m.content : '';
                    let derivedTitle = url;
                    try {
                      if (url.startsWith('file://')) {
                        const last = url.split('/').filter(Boolean).pop() || url;
                        derivedTitle = decodeURIComponent(last);
                      } else if (url.startsWith('http://') || url.startsWith('https://')) {
                        const u = new URL(url);
                        const segs = u.pathname.split('/').filter(Boolean);
                        const last = segs.length ? decodeURIComponent(segs[segs.length - 1]) : '';
                        derivedTitle = last ? `${last} · ${u.hostname}` : u.hostname;
                      }
                    } catch { /* keep url as-is */ }
                    const displayTitle = m.filename || customTitle || derivedTitle;
                    const sizeLabel = m.file_size ? ` · ${(m.file_size / 1024 < 1024) ? `${Math.round(m.file_size / 1024)} KB` : `${(m.file_size / (1024 * 1024)).toFixed(1)} MB`}` : '';
                    return (
                      <div key={m.message_id} className="group flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100" data-testid={`research-item-${m.message_id}`}>
                        {isUpload ? <FileText className="h-5 w-5 text-brand-sage mt-0.5 flex-shrink-0" /> : <Link2 className="h-5 w-5 text-brand-navy mt-0.5 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <a href={url} target="_blank" rel="noopener noreferrer"
                            className="text-sm font-medium text-brand-navy hover:underline break-words" title={url}>{displayTitle}</a>
                          <p className="text-xs text-slate-400 mt-1 flex items-center gap-1.5 flex-wrap">
                            {isUpload && <span className="inline-block px-1.5 py-0.5 rounded bg-brand-sage/10 text-brand-sage font-semibold text-[9px]">UPLOAD</span>}
                            <span>{m.sender_name} — {new Date(m.timestamp).toLocaleDateString()}{sizeLabel}</span>
                            {(() => {
                              const fid = m.folder_id || folders.find(f => f.is_default)?.folder_id;
                              const folder = folders.find(f => f.folder_id === fid);
                              if (!folder) return null;
                              return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-medium text-[9px]">📁 {folder.name}</span>;
                            })()}
                          </p>
                        </div>
                        {(isOwner || isAdmin) && folders.length > 1 && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs flex-shrink-0 text-slate-500 hover:text-slate-800"
                                data-testid={`move-research-${m.message_id}`}>
                                Move →
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400">Move to folder</DropdownMenuLabel>
                              {folders.map(f => {
                                const currentFid = m.folder_id || folders.find(x => x.is_default)?.folder_id;
                                if (f.folder_id === currentFid) return null;
                                return (
                                  <DropdownMenuItem key={f.folder_id} onClick={() => handleMoveResearch(m, f.folder_id)}
                                    data-testid={`move-target-${m.message_id}-${f.folder_id}`}>
                                    <FileText className="h-3.5 w-3.5 mr-1.5 text-slate-400" /> {f.name}
                                  </DropdownMenuItem>
                                );
                              })}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        {(isOwner || isAdmin) && (
                          deleteResearchConfirm === m.message_id ? (
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <span className="text-xs text-rose-600 font-medium hidden sm:inline">Delete?</span>
                              <Button size="sm" variant="destructive"
                                data-testid={`confirm-delete-research-${m.message_id}`}
                                onClick={() => handleDeleteResearch(m)}
                                className="h-7 px-2 text-xs">Yes</Button>
                              <Button size="sm" variant="outline"
                                data-testid={`cancel-delete-research-${m.message_id}`}
                                onClick={() => setDeleteResearchConfirm(null)}
                                className="h-7 px-2 text-xs">No</Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="ghost"
                              className="h-7 px-2 text-rose-500 hover:text-rose-700 hover:bg-rose-50 flex-shrink-0"
                              data-testid={`delete-research-${m.message_id}`}
                              title={isAdmin ? 'Delete (admin can restore from Admin > BriteLabs > Research Management)' : 'Delete (admin can restore)'}
                              onClick={() => setDeleteResearchConfirm(m.message_id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )
                        )}
                      </div>
                    );
                  });
                  })()}
                </div>
                {canInteract && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input data-testid="research-url-input" placeholder="Paste research URL or article link..."
                        value={researchUrl} onChange={e => setResearchUrl(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && sendMessage('research')} />
                      <Button data-testid="add-research-btn" onClick={() => sendMessage('research')} variant="outline" size="sm">
                        <Plus className="h-4 w-4 mr-1" /> Add link
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <span className="h-px bg-slate-200 flex-1" /><span>or</span><span className="h-px bg-slate-200 flex-1" />
                    </div>
                    <div>
                      <input ref={researchFileInputRef} type="file" multiple className="hidden"
                        data-testid="research-file-input"
                        onChange={async (e) => {
                          const fileList = Array.from(e.target.files || []);
                          e.target.value = '';
                          if (!fileList.length) return;
                          const fd = new FormData();
                          fileList.forEach(f => fd.append('files', f));
                          if (activeFolderId && activeFolderId !== 'all') {
                            fd.append('folder_id', activeFolderId);
                          }
                          try {
                            setUploadingResearch(true);
                            const res = await fetch(`${BACKEND_URL}/api/britelabs/${labId}/research/upload`, {
                              method: 'POST', credentials: 'include',
                              headers: token ? { Authorization: `Bearer ${token}` } : {},
                              body: fd,
                            });
                            if (!res.ok) {
                              const err = await res.json().catch(() => ({}));
                              throw new Error(err.detail || 'Upload failed');
                            }
                            const d = await res.json();
                            toast.success(`Uploaded ${d.count} document${d.count === 1 ? '' : 's'}`);
                            loadMessages();
                            loadFolders();
                          } catch (err) {
                            toast.error(err.message || 'Upload failed');
                          } finally {
                            setUploadingResearch(false);
                          }
                        }} />
                      <Button variant="outline" size="sm" className="w-full"
                        onClick={() => researchFileInputRef.current?.click()}
                        disabled={uploadingResearch}
                        data-testid="research-upload-btn">
                        <Plus className="h-4 w-4 mr-1" />
                        {uploadingResearch ? 'Uploading…' : 'Upload document(s)'}
                      </Button>
                      <p className="text-[10px] text-slate-400 mt-1 text-center">PDF, Word, Excel, images, or any file up to 25MB each. Hold Cmd/Ctrl to pick multiple.</p>
                    </div>
                  </div>
                )}
                </Card>
              </div>

              {/* Recommended Reads */}
              {recommendedReads.length > 0 && (
                <Card className="border border-slate-200 p-5 mt-4" data-testid="recommended-reads">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="h-4 w-4 text-brand-sage" />
                    <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Recommended Reads</h3>
                  </div>
                  <p className="text-xs text-slate-500 mb-4">Suggested from news, the research library, and other BriteLabs based on this lab's topic.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {recommendedReads.map((r, idx) => {
                      const typeStyle = r.type === 'news'
                        ? { label: 'NEWS', color: 'bg-rose-50 text-rose-700 border-rose-100' }
                        : r.type === 'research'
                          ? { label: 'RESEARCH', color: 'bg-indigo-50 text-indigo-700 border-indigo-100' }
                          : { label: 'BRITELAB', color: 'bg-emerald-50 text-emerald-700 border-emerald-100' };
                      const isInternal = r.link && r.link.startsWith('/');
                      const linkProps = isInternal
                        ? { onClick: () => navigate(r.link), role: 'button' }
                        : { href: r.link, target: '_blank', rel: 'noopener noreferrer' };
                      const Tag = isInternal ? 'div' : 'a';
                      return (
                        <Tag key={`${r.type}-${r.id}-${idx}`} {...linkProps}
                          className="block rounded-lg overflow-hidden border border-slate-200 hover:border-brand-navy/40 hover:shadow-sm transition-all cursor-pointer group"
                          data-testid={`reco-read-${r.type}-${idx}`}>
                          {r.image && r.type === 'news' && (
                            <div className="aspect-video bg-slate-100 overflow-hidden">
                              <img src={r.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                            </div>
                          )}
                          <div className="p-3">
                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold border ${typeStyle.color}`}>{typeStyle.label}</span>
                            <p className="text-sm font-semibold text-slate-800 mt-2 line-clamp-2 group-hover:text-brand-navy">{r.title}</p>
                            {r.summary && <p className="text-xs text-slate-500 mt-1 line-clamp-3">{r.summary}</p>}
                            {r.meta && <p className="text-[10px] text-slate-400 mt-2">{r.meta}</p>}
                          </div>
                        </Tag>
                      );
                    })}
                  </div>
                </Card>
              )}
            </TabsContent>

            {/* Project Plan Tab */}
            <TabsContent value="plan">
              <Card className="border border-slate-200 p-3 md:p-5">
                <div className="flex items-center justify-between mb-3 md:mb-4 gap-2 flex-wrap">
                  <div className="flex items-center gap-3">
                    <Label className="text-sm font-semibold">Enable Project Plan</Label>
                    <Switch checked={hasPlan} onCheckedChange={(v) => {
                      setHasPlan(v);
                      if (v && labParticipants.length === 0) {
                        const memberIds = new Set([...(lab?.invited_users || []), lab?.created_by].filter(Boolean));
                        fetch(`${BACKEND_URL}/api/members`, { credentials: 'include', headers })
                          .then(r => r.json()).then(users => setLabParticipants(users.filter(u => memberIds.has(u.user_id)))).catch(() => {});
                      }
                    }} disabled={!canEdit} />
                  </div>
                  {hasPlan && canEdit && (
                    <Button onClick={addMilestone} variant="outline" size="sm"><Plus className="h-4 w-4 mr-1" /> Add Milestone</Button>
                  )}
                </div>
                {hasPlan ? (
                  <div className="space-y-3">
                    {milestones.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No milestones yet.</p>}
                    {/* Active milestones — sorted by due date (most urgent first) */}
                    {milestones
                      .map((m, origIdx) => ({ ...m, origIdx }))
                      .filter(m => m.status !== 'complete')
                      .sort((a, b) => {
                        if (!a.due_date && !b.due_date) return 0;
                        if (!a.due_date) return 1;
                        if (!b.due_date) return -1;
                        return a.due_date.localeCompare(b.due_date);
                      })
                      .map((m) => {
                        const i = m.origIdx;
                        return (
                      <div key={i} className="p-3 md:p-4 bg-slate-50 rounded-lg border border-slate-100 space-y-2 md:space-y-3" data-testid={`milestone-${i}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${m.status === 'in_progress' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                              {m.status === 'in_progress' ? 'In Progress' : 'Pending'}
                            </span>
                            <PriorityPill priority={m.priority} />
                            {m.due_date && (
                              <span className={`text-xs ${new Date(m.due_date) < new Date() ? 'text-red-500 font-semibold' : 'text-slate-400'}`}>
                                Due: {new Date(m.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                            )}
                          </div>
                          {canEdit && (
                            <button onClick={() => removeMilestone(i)} className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                          )}
                        </div>
                        <Input placeholder="Milestone title" value={m.title} data-testid={`milestone-title-${i}`}
                          onChange={e => updateMilestone(i, 'title', e.target.value)} disabled={!canEdit} />

                        {/* Assigned Members */}
                        <div>
                          <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Assigned Members</Label>
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {(m.owners || []).map(o => (
                              <span key={o.user_id || o.name} className="inline-flex items-center gap-1 bg-brand-navy/10 text-brand-navy text-xs font-medium px-2.5 py-1 rounded-full" data-testid={`milestone-owner-${i}-${o.user_id}`}>
                                {o.picture ? <MemberAvatar name={o.name} picture={o.picture} size={16} /> : null}
                                {o.name}
                                {canEdit && (
                                  <button onClick={() => removeMilestoneOwner(i, o.user_id)} className="hover:text-red-500 ml-0.5"><X className="h-3 w-3" /></button>
                                )}
                              </span>
                            ))}
                            {(m.owners || []).length === 0 && <span className="text-xs text-slate-400">No members assigned</span>}
                          </div>
                          {canEdit && (
                            <div className="relative" onClick={e => e.stopPropagation()}>
                              <Input
                                placeholder="Search BriteLab members…"
                                value={milestoneSearch[i] || ''}
                                onChange={e => { setMilestoneSearch({...milestoneSearch, [i]: e.target.value}); setMilestoneDropdown(i); }}
                                onFocus={() => setMilestoneDropdown(i)}
                                data-testid={`milestone-member-search-${i}`}
                                className="text-sm"
                              />
                              {milestoneDropdown === i && (
                                <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto" data-testid={`milestone-member-dropdown-${i}`}>
                                  {labParticipants
                                    .filter(p => {
                                      const q = (milestoneSearch[i] || '').toLowerCase();
                                      const alreadyAdded = (m.owners || []).some(o => o.user_id === p.user_id);
                                      return !alreadyAdded && (!q || p.name?.toLowerCase().includes(q) || p.profile?.role?.toLowerCase().includes(q));
                                    })
                                    .slice(0, 8)
                                    .map(p => (
                                      <button key={p.user_id} type="button" className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left text-sm"
                                        data-testid={`milestone-add-member-${i}-${p.user_id}`}
                                        onClick={(e) => { e.stopPropagation(); addMilestoneOwner(i, p); setMilestoneSearch({...milestoneSearch, [i]: ''}); }}>
                                        <MemberAvatar name={p.name} picture={p.picture} size={24} />
                                        <div><span className="font-medium">{p.name}</span>{p.profile?.role ? <span className="text-slate-400 ml-1">- {p.profile.role}</span> : null}</div>
                                      </button>
                                    ))
                                  }
                                  {labParticipants.filter(p => !(m.owners || []).some(o => o.user_id === p.user_id)).length === 0 && (
                                    <p className="px-3 py-2 text-xs text-slate-400">All participants assigned</p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="grid md:grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs font-semibold text-slate-500 mb-1.5 flex items-center gap-1.5">
                              <CalendarIcon className="h-3.5 w-3.5" /> Completed by
                            </Label>
                            <Input type="date" value={m.due_date} data-testid={`milestone-date-${i}`}
                              onChange={e => updateMilestone(i, 'due_date', e.target.value)} disabled={!canEdit} />
                          </div>
                          <div>
                            <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Status</Label>
                            <Select value={m.status} onValueChange={v => updateMilestone(i, 'status', v)} disabled={!canEdit}>
                              <SelectTrigger data-testid={`milestone-status-${i}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="complete">Complete</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Priority</Label>
                            <Select value={m.priority || 'medium'} onValueChange={v => updateMilestone(i, 'priority', v)} disabled={!canEdit}>
                              <SelectTrigger data-testid={`milestone-priority-${i}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="low">Low</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Description / Success Criteria</Label>
                          <Textarea placeholder="Describe this milestone, success criteria, deliverables..." value={m.description || m.success_criteria || ''} rows={4}
                            data-testid={`milestone-description-${i}`}
                            onChange={e => updateMilestone(i, 'description', e.target.value)} disabled={!canEdit}
                            className="resize-y min-h-[100px] md:min-h-[160px]" />
                        </div>
                      </div>
                        );
                      })}

                    {/* Archived / Completed Milestones */}
                    {milestones.filter(m => m.status === 'complete').length > 0 && (
                      <div className="mt-4">
                        <button onClick={() => setShowArchivedMilestones(!showArchivedMilestones)}
                          className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-700 transition-colors w-full"
                          data-testid="toggle-archived-milestones">
                          <CheckCircle className="h-4 w-4 text-brand-sage" />
                          {milestones.filter(m => m.status === 'complete').length} Completed Milestone{milestones.filter(m => m.status === 'complete').length !== 1 ? 's' : ''}
                          <span className="text-xs text-slate-400 ml-auto">{showArchivedMilestones ? 'Hide' : 'View'}</span>
                        </button>
                        {showArchivedMilestones && (
                          <div className="space-y-2 mt-2">
                            {milestones
                              .map((m, origIdx) => ({ ...m, origIdx }))
                              .filter(m => m.status === 'complete')
                              .map((m) => (
                                <div key={m.origIdx} className="p-3 bg-green-50/50 rounded-lg border border-green-100" data-testid={`archived-milestone-${m.origIdx}`}>
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <CheckCircle className="h-4 w-4 text-brand-sage" />
                                    <h4 className="text-sm font-semibold text-slate-600 line-through">{m.title || 'Untitled'}</h4>
                                    {m.priority && <PriorityPill priority={m.priority} />}
                                    {m.due_date && <span className="text-xs text-slate-400 ml-auto">Completed by {new Date(m.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>}
                                  </div>
                                  {m.owners?.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {m.owners.map(o => (
                                        <span key={o.user_id || o.name} className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{o.name}</span>
                                      ))}
                                    </div>
                                  )}
                                  {m.description && <p className="text-xs text-slate-500 mt-1.5">{m.description}</p>}
                                  {canEdit && (
                                    <div className="flex justify-end mt-2">
                                      <button type="button" onClick={() => updateMilestone(m.origIdx, 'status', 'in_progress')}
                                        className="text-[10px] font-semibold text-amber-700 hover:text-amber-800 underline"
                                        data-testid={`reopen-milestone-${m.origIdx}`}>
                                        Re-open
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    )}

                    {(isOwner || isAdmin) && milestones.length > 0 && (
                      <Button data-testid="save-milestones-btn" onClick={saveMilestones}
                        className="w-full bg-gradient-to-r from-brand-navy to-brand-sage text-white mt-2">Save Plan</Button>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-6">Project plan is not enabled for this BriteLab.</p>
                )}
              </Card>
            </TabsContent>

            {/* Diary Tab — meetings, deadlines, recent activity */}
            <TabsContent value="calendar">
              <Card className="p-4 md:p-6 border border-slate-200" data-testid="calendar-tab-content">
                <BriteLabCalendarTab
                  labId={lab.lab_id}
                  currentUserId={user?.user_id}
                  isMember={isInvitedMember || isAdmin}
                />
              </Card>
            </TabsContent>

            {/* Mini Projects Tab — only available on parent BriteLabs */}
            {!lab.parent_lab_id && (
              <TabsContent value="minis">
                <Card className="p-4 md:p-6 border border-slate-200" data-testid="minis-tab-content">
                  <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                    <div>
                      <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <GitBranch className="h-5 w-5 text-violet-600" /> Mini Projects
                      </h3>
                      <p className="text-xs md:text-sm text-slate-500 mt-1">
                        Sandboxed sub-workstreams under this BriteLab. Each Mini Project has its own invitees, chat and files.
                        Parent stakeholders see everything; mini-only invitees stay scoped to their mini.
                      </p>
                    </div>
                    {(isInvitedMember || isAdmin) && canInteract && (
                      <Button onClick={() => setShowCreateMini(true)} data-testid="create-mini-btn"
                        className="bg-violet-600 hover:bg-violet-700 text-white" size="sm">
                        <Plus className="h-4 w-4 mr-1" /> Create Mini Project
                      </Button>
                    )}
                  </div>

                  {minis.length === 0 ? (
                    <div className="text-center py-10 text-slate-400 text-sm" data-testid="minis-empty-state">
                      No Mini Projects yet. Spin one up to keep a focused team working on a sub-problem without exposing the parent BriteLab.
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-3" data-testid="minis-list">
                      {minis.map(m => (
                        <button key={m.lab_id} onClick={() => navigate(`/britelabs/${m.lab_id}`)}
                          data-testid={`mini-card-${m.lab_id}`}
                          className="text-left p-4 rounded-lg border border-slate-200 hover:border-violet-300 hover:shadow-sm transition-all bg-white">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="font-semibold text-slate-800 line-clamp-2">{m.title}</div>
                            <Badge variant="outline" className={`text-[10px] flex-shrink-0 ${
                              m.status === 'active' ? 'border-brand-sage text-brand-sage' :
                              m.status === 'solved' ? 'border-brand-blue text-brand-navy' : 'border-slate-300 text-slate-500'
                            }`}>{m.status}</Badge>
                          </div>
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {m.category && <Badge variant="outline" className="text-[10px]">{m.category}</Badge>}
                            {m.impact_level && (
                              <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-100 capitalize">
                                {m.impact_level.replace(/_/g, ' ')}
                              </Badge>
                            )}
                            {m.access === 'parent_inherited' && (
                              <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-100">Inherited</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-[11px] text-slate-500">
                            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {m.stakeholder_count}</span>
                            <span className="inline-flex items-center gap-1"><Target className="h-3 w-3" /> {m.milestones_done}/{m.milestones_total}</span>
                            <span className="ml-auto text-slate-400">{m.creator_name}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </Card>
              </TabsContent>
            )}
          </Tabs>
            </div>
            <PageSideRail placement={`britelab:${labId}`} />
          </div>
        </div>
      </main>

      {/* Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="invite-dialog">
          <DialogHeader><DialogTitle>Invite Stakeholders</DialogTitle></DialogHeader>

          {/* Tab toggle */}
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-3">
            <button onClick={() => setInviteTab('recommended')} data-testid="invite-tab-recommended"
              className={`flex-1 text-sm font-medium py-2 rounded-md transition-colors flex items-center justify-center gap-1.5 ${
                inviteTab === 'recommended' ? 'bg-white text-brand-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>
              <Sparkles className="h-3.5 w-3.5" /> Recommended ({recommendedUsers.length})
            </button>
            <button onClick={() => setInviteTab('all')} data-testid="invite-tab-all"
              className={`flex-1 text-sm font-medium py-2 rounded-md transition-colors flex items-center justify-center gap-1.5 ${
                inviteTab === 'all' ? 'bg-white text-brand-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>
              <Users className="h-3.5 w-3.5" /> All Members
            </button>
          </div>

          {inviteTab === 'all' && (
            <Input data-testid="invite-search-input" placeholder="Search members by name or email..."
              value={userSearch} onChange={e => setUserSearch(e.target.value)} className="mb-3" />
          )}

          <div className="max-h-72 overflow-y-auto space-y-1.5" data-testid="invite-user-list">
            {inviteTab === 'recommended' ? (
              recommendedUsers.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No recommendations available yet</p>
                  <p className="text-xs text-slate-400 mt-1">Try adding country and category to your BriteLab</p>
                </div>
              ) : (
                recommendedUsers.map(u => (
                  <label key={u.user_id} data-testid={`recommended-user-${u.user_id}`}
                    className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all border ${
                      selectedInvites.includes(u.user_id) ? 'bg-brand-navy/5 border-brand-navy/20' : 'bg-white border-slate-100 hover:border-slate-200 hover:bg-slate-50'
                    }`}>
                    <input type="checkbox" checked={selectedInvites.includes(u.user_id)}
                      onChange={e => setSelectedInvites(e.target.checked
                        ? [...selectedInvites, u.user_id] : selectedInvites.filter(id => id !== u.user_id)
                      )} className="rounded mt-1" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <MemberAvatar name={u.name} picture={u.picture} size={28} />
                        <span className="text-sm font-semibold text-slate-800 truncate">{u.name}</span>
                        {u.country && COUNTRIES_MAP[u.country] && (
                          <span className="text-sm" title={u.country}>{COUNTRIES_MAP[u.country]}</span>
                        )}
                      </div>
                      {(u.role || u.organisation) && (
                        <p className="text-xs text-slate-500 ml-9 truncate">{[u.role, u.organisation].filter(Boolean).join(' at ')}</p>
                      )}
                      {/* Match reasons */}
                      <div className="flex flex-wrap gap-1.5 mt-1.5 ml-9">
                        {u.match_reasons?.includes('location') && (
                          <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700 border border-green-100">
                            <MapPin className="h-2.5 w-2.5" /> Location match
                          </span>
                        )}
                        {u.match_reasons?.includes('expertise') && (
                          <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-100">
                            <Sparkles className="h-2.5 w-2.5" /> Expertise match
                          </span>
                        )}
                        {u.match_reasons?.includes('sector') && (
                          <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-100">
                            Sector match
                          </span>
                        )}
                        {u.behavioural_reasons?.map(r => (
                          <span key={r} className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-100" data-testid={`behav-reason-${u.user_id}`}>
                            <Sparkles className="h-2.5 w-2.5" /> {r}
                          </span>
                        ))}
                      </div>
                      {u.matched_skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1 ml-9">
                          {u.matched_skills.slice(0, 4).map(s => (
                            <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 capitalize">{s}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </label>
                ))
              )
            ) : (
              filteredInviteUsers.slice(0, 30).map(u => (
                <label key={u.user_id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 cursor-pointer border border-transparent hover:border-slate-100">
                  <input type="checkbox" checked={selectedInvites.includes(u.user_id)}
                    onChange={e => setSelectedInvites(e.target.checked
                      ? [...selectedInvites, u.user_id] : selectedInvites.filter(id => id !== u.user_id)
                    )} className="rounded" />
                  <MemberAvatar name={u.name} picture={u.picture} size={28} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{u.name}</p>
                    <p className="text-xs text-slate-400 truncate">{u.email}</p>
                  </div>
                </label>
              ))
            )}
          </div>

          {/* Invite by email section */}
          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-500 mb-1.5">Or invite by email (for non-members too)</p>
            <textarea
              data-testid="email-invite-input"
              value={emailInvites}
              onChange={(e) => setEmailInvites(e.target.value)}
              placeholder="Enter email addresses (comma or newline separated)"
              className="w-full text-sm border border-slate-200 rounded-lg p-2.5 min-h-[60px] resize-none focus:ring-2 focus:ring-brand-navy/20 focus:border-brand-navy"
            />
          </div>

          {(selectedInvites.length > 0 || emailInvites.trim()) && (
            <div className="pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-500 mb-2">
                {selectedInvites.length > 0 && `${selectedInvites.length} member${selectedInvites.length !== 1 ? 's' : ''} selected`}
                {selectedInvites.length > 0 && emailInvites.trim() && ' + '}
                {emailInvites.trim() && `${emailInvites.split(/[,;\n]+/).filter(e => e.trim()).length} email invite${emailInvites.split(/[,;\n]+/).filter(e => e.trim()).length !== 1 ? 's' : ''}`}
              </p>
              <Button data-testid="confirm-invite-btn" onClick={inviteUsers}
                className="w-full bg-brand-navy text-white">
                Send Invitations
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Solve Dialog */}
      <Dialog open={showSolve} onOpenChange={setShowSolve}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Resolve BriteLab</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Outcome Type</Label>
              <Select value={solveForm.outcome_type} onValueChange={v => setSolveForm({ ...solveForm, outcome_type: v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="What was achieved?" /></SelectTrigger>
                <SelectContent>
                  {OUTCOME_TYPES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Solution Summary</Label>
              <Textarea placeholder="Describe the solution or outcome..."
                value={solveForm.solution_summary} onChange={e => setSolveForm({ ...solveForm, solution_summary: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={solveForm.status} onValueChange={v => setSolveForm({ ...solveForm, status: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="solved">Solved</SelectItem>
                  <SelectItem value="closed">Closed (No Solution)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="confirm-solve-btn" onClick={handleSolve}
              className="w-full bg-brand-sage hover:bg-brand-sage text-white">Confirm</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ReportDialog
        open={!!reportTarget}
        onOpenChange={() => setReportTarget(null)}
        reportedUserId={reportTarget?.userId}
        reportedUserName={reportTarget?.userName}
        contextType={reportTarget?.contextType}
        contextId={reportTarget?.contextId}
      />

      {/* Member Profile Modal — opens in-place, doesn't navigate away from BriteLab */}
      <MemberProfileModal
        member={viewingMember}
        open={!!viewingMember}
        onClose={() => setViewingMember(null)}
        user={user}
      />

      {/* Add Milestone Dialog */}
      <Dialog open={showMilestoneDialog} onOpenChange={setShowMilestoneDialog}>
        <DialogContent className="max-w-lg" data-testid="add-milestone-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-brand-navy" /> Add Milestone
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Title</Label>
              <Input placeholder="Milestone title" value={newMilestone.title}
                onChange={e => setNewMilestone(p => ({ ...p, title: e.target.value }))}
                data-testid="new-milestone-title" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Due Date</Label>
                <Input type="date" value={newMilestone.due_date}
                  onChange={e => setNewMilestone(p => ({ ...p, due_date: e.target.value }))}
                  data-testid="new-milestone-date" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Status</Label>
                <Select value={newMilestone.status} onValueChange={v => setNewMilestone(p => ({ ...p, status: v }))}>
                  <SelectTrigger data-testid="new-milestone-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Priority</Label>
                <Select value={newMilestone.priority || 'medium'} onValueChange={v => setNewMilestone(p => ({ ...p, priority: v }))}>
                  <SelectTrigger data-testid="new-milestone-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Responsible Member(s)</Label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {(newMilestone.owners || []).map(o => (
                  <span key={o.user_id} className="inline-flex items-center gap-1 bg-brand-navy/10 text-brand-navy text-xs font-medium px-2.5 py-1 rounded-full" data-testid={`new-milestone-owner-${o.user_id}`}>
                    {o.picture && <MemberAvatar name={o.name} picture={o.picture} size={16} />}
                    {o.name}
                    <button type="button" onClick={() => removeNewMilestoneOwner(o.user_id)} className="ml-1 hover:text-red-500" data-testid={`new-milestone-remove-owner-${o.user_id}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                {(newMilestone.owners || []).length === 0 && (
                  <span className="text-xs text-slate-400">No one assigned yet — search below to add.</span>
                )}
              </div>
              <div className="relative">
                <Input
                  placeholder="Search BriteLab members…"
                  value={newMilestoneSearch}
                  onChange={e => { setNewMilestoneSearch(e.target.value); setNewMilestoneDropdownOpen(true); }}
                  onFocus={() => setNewMilestoneDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setNewMilestoneDropdownOpen(false), 150)}
                  className="text-sm"
                  data-testid="new-milestone-member-search"
                />
                {newMilestoneDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-44 overflow-y-auto" data-testid="new-milestone-member-dropdown">
                    {(() => {
                      const q = newMilestoneSearch.toLowerCase();
                      const taken = new Set((newMilestone.owners || []).map(o => o.user_id));
                      const candidates = labParticipants.filter(p => !taken.has(p.user_id) && (q === '' || p.name?.toLowerCase().includes(q)));
                      if (candidates.length === 0) {
                        return <p className="px-3 py-2 text-xs text-slate-400">No matching BriteLab members.</p>;
                      }
                      return candidates.slice(0, 8).map(p => (
                        <button key={p.user_id} type="button"
                          onClick={() => addNewMilestoneOwner(p)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
                          data-testid={`new-milestone-add-member-${p.user_id}`}>
                          <MemberAvatar name={p.name} picture={p.picture} size={24} />
                          <span>{p.name}</span>
                        </button>
                      ));
                    })()}
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Description / Success Criteria</Label>
              <Textarea placeholder="Describe this milestone..." value={newMilestone.description}
                onChange={e => setNewMilestone(p => ({ ...p, description: e.target.value }))}
                rows={3} data-testid="new-milestone-description" />
            </div>
            <Button onClick={confirmAddMilestone} disabled={!newMilestone.title.trim()}
              className="w-full bg-brand-navy text-white" data-testid="confirm-add-milestone-btn">
              Add Milestone
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Mini Project Dialog */}
      <Dialog open={showCreateMini} onOpenChange={setShowCreateMini}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-mini-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="h-5 w-5 text-violet-600" /> Create Mini Project
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 mb-3">
            A Mini Project sits under <span className="font-medium text-slate-700">{lab?.title}</span>. Parent stakeholders will automatically see it, while invitees you add to the mini will only see this workstream.
          </p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Title *</Label>
              <Input value={miniForm.title} onChange={e => setMiniForm(f => ({ ...f, title: e.target.value }))}
                placeholder="What's the focused sub-problem?" data-testid="mini-title-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Root Cause *</Label>
              <RichTextEditor value={miniForm.root_cause} onChange={v => setMiniForm(f => ({ ...f, root_cause: v }))}
                minHeight="4rem" placeholder="Why does this need its own workstream?" testId="mini-root-cause-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Problem Statement *</Label>
              <RichTextEditor value={miniForm.problem_statement} onChange={v => setMiniForm(f => ({ ...f, problem_statement: v }))}
                minHeight="6rem" placeholder="Describe the scope of this mini..." testId="mini-problem-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Desired Outcome</Label>
              <RichTextEditor value={miniForm.desired_outcome} onChange={v => setMiniForm(f => ({ ...f, desired_outcome: v }))}
                minHeight="4rem" placeholder="What does success look like?" testId="mini-outcome-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Category *</Label>
                <Select value={miniForm.category} onValueChange={v => setMiniForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="mini-category-select"><SelectValue placeholder="Pick category" /></SelectTrigger>
                  <SelectContent>
                    {labCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Impact Level</Label>
                <Select value={miniForm.impact_level} onValueChange={v => setMiniForm(f => ({ ...f, impact_level: v }))}>
                  <SelectTrigger data-testid="mini-impact-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    {IMPACT_LEVELS.map(il => <SelectItem key={il.value} value={il.value}>{il.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Lab Type</Label>
              <div className="flex gap-2">
                {['problem', 'question'].map(t => (
                  <button key={t} type="button" onClick={() => setMiniForm(f => ({ ...f, lab_type: t }))}
                    data-testid={`mini-lab-type-${t}`}
                    className={`px-3 py-1.5 rounded-md text-sm border transition-colors capitalize ${
                      miniForm.lab_type === t ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-slate-600 border-slate-200 hover:border-brand-blue'
                    }`}>{t}</button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleCreateMini} disabled={creatingMini}
                className="flex-1 bg-violet-600 hover:bg-violet-700 text-white" data-testid="submit-mini-btn">
                {creatingMini ? 'Creating…' : 'Create Mini Project'}
              </Button>
              <Button variant="outline" onClick={() => setShowCreateMini(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
